import { useUser } from "$global-providers/user-provider";
import { usePaymentStatus } from "$routes/billing/_lib/pre-service/use-payment-status";
import type { InvestigationActivityFragmentFragment } from "$routes/patient/[:patientId]/inpatient/investigations/_lib/__generated__/graphql.gql";
import {
  type InvestigationActivity,
  PriceableItemOrigin,
  Role,
  type VisitInvestigationActivity,
} from "elephant-web/gql";
import React from "react";
import DynamicInvestigationResults from "./dynamic-investigation-results/dynamic-investigation-results";
import InvestigationActivityCard from "./investigation-activity-card";
import { InvestigationCardFormForVisit } from "./investigation-activity-card-form";
import type { InvestigationRequestOutcome } from "./investigation-activity-type";

const ROLES_FOR_FORM = [Role.Nurse, Role.Doctor, Role.Technician];

interface InvestigationActivityOutcomeProps {
  subtitle?: string;
  investigation:
    | VisitInvestigationActivity
    | InvestigationActivity
    | InvestigationActivityFragmentFragment;
  defaultExpanded?: boolean;
  showStatus?: boolean;
  disabled?: boolean;
  enableDelete?: boolean;
  patientUuid: string;
}

const InvestigationActivityOutcome = ({
  investigation,
  subtitle,
  defaultExpanded,
  showStatus,
  disabled,
  enableDelete,
  patientUuid,
}: InvestigationActivityOutcomeProps): React.ReactElement => {
  const { role } = useUser();
  const canCompleteForm = ROLES_FOR_FORM.includes(role);

  const { catalogueItem, request, outcome } = investigation;
  // `id` on the visit-scoped variant is exposed only as the legacy `_id` alias.
  const catalogueItemId =
    "id" in catalogueItem ? catalogueItem.id : catalogueItem._id;
  const paymentStatus = usePaymentStatus({
    patientId: patientUuid,
    externalReference: catalogueItemId
      ? {
          origin: PriceableItemOrigin.Investigation,
          foreignId: catalogueItemId,
        }
      : null,
  });
  const { performer: requester, meta, location: requestLocation } = request;
  const requestedDate = meta?.created ?? investigation.meta?.created;

  const fulfiller = outcome?.performer;
  const fulfilledDate = outcome?.meta?.created ?? investigation.meta?.created;
  const outcomeLocation = outcome?.location;
  const { title } = catalogueItem;

  // Only outcome will contain resulting values
  const isRequest = !outcome;
  const shouldRenderForm = isRequest && canCompleteForm;
  const property = outcome ?? request;

  return (
    <InvestigationActivityCard
      investigationActivityId={investigation._id}
      title={title}
      subtitle={subtitle}
      requester={requester}
      requestedDate={requestedDate}
      fulfiller={fulfiller}
      fulfilledDate={fulfilledDate}
      isRequested={!outcome}
      defaultExpanded={defaultExpanded ?? shouldRenderForm}
      requestLocation={requestLocation}
      outcomeLocation={outcomeLocation}
      showStatus={showStatus}
      enableDelete={enableDelete}
      patientUuid={patientUuid}
      paymentStatus={paymentStatus}
    >
      {shouldRenderForm ? (
        <InvestigationCardFormForVisit
          investigation_id={investigation._id}
          investigationRequest={request}
          disabled={disabled}
          paymentBlocked={paymentStatus === "awaiting"}
          patientUuid={patientUuid}
        />
      ) : (
        <DynamicInvestigationResults
          property={property as unknown as InvestigationRequestOutcome}
        />
      )}
    </InvestigationActivityCard>
  );
};

InvestigationActivityOutcome.defaultProps = {
  disabled: false,
  defaultExpanded: null,
  subtitle: null,
  showStatus: true,
  enableDelete: false,
};

export default InvestigationActivityOutcome;
